/**
 * Apply custom format
 *
 * @param body - The post body is a plain text string.
 * @param customFormat - An array with format ranges parsed as a string.
 * @returns The body with custom format applied.
 */

type InlineType = 'bold' | 'italic' | 'underline';
type FormatRange = { start: number; end: number; type?: string };
type CustomFormat = {
  [key in InlineType | 'list']?: FormatRange[];
};

export function applyCustomFormat(
  body: string,
  customFormat: string | CustomFormat
): string {
  if (!customFormat) return body;

  const customFormatParsed: CustomFormat =
    typeof customFormat === 'string' ? JSON.parse(customFormat) : customFormat;

  const lines = body.split('\n');
  const listRanges = customFormatParsed.list || [];

  let html = '';
  let insideList = false;
  let charOffset = 0;

  lines.forEach((line) => {
    const lineLength = line.length + 1; // +1 for \n
    const charStart = charOffset;
    const charEnd = charOffset + lineLength;

    const isBullet = listRanges.some(
      ({ start, end }) => charStart < end && charEnd > start
    );

    const inlineFormattedLine = applyInlineFormatting(
      line,
      customFormatParsed,
      charStart
    );

    if (isBullet) {
      if (!insideList) {
        html += '<ul>';
        insideList = true;
      }
      html += `<li>${inlineFormattedLine}</li>`;
    } else {
      if (insideList) {
        html += '</ul>';
        insideList = false;
      }
      html += inlineFormattedLine + '<br>'; // Preserve line breaks
    }

    charOffset += lineLength;
  });

  if (insideList) html += '</ul>';

  return html;
}

function applyInlineFormatting(
  line: string,
  customFormat: CustomFormat,
  lineOffset = 0
): string {
  if (!customFormat) return line;

  const formats: InlineType[][] = Array.from({ length: line.length }, () => []);

  for (const [type, ranges] of Object.entries(customFormat) as [
    string,
    FormatRange[]
  ][]) {
    if (type === 'list') continue; // Skip list
    for (const { start, end } of ranges) {
      const lineStart = 0;
      const lineEnd = line.length;
      const overlapStart = Math.max(start - lineOffset, lineStart);
      const overlapEnd = Math.min(end - lineOffset, lineEnd);

      for (let i = overlapStart; i < overlapEnd; i++) {
        if (i >= 0 && i < line.length) formats[i].push(type as InlineType);
      }
    }
  }

  let result = '';
  let prevFormats: InlineType[] = [];

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const currFormats = formats[i];

    // Close formats no longer active
    for (let j = prevFormats.length - 1; j >= 0; j--) {
      if (!currFormats.includes(prevFormats[j])) {
        result += closeInlineTag(prevFormats[j]);
      }
    }

    // Open new formats
    for (const f of currFormats) {
      if (!prevFormats.includes(f)) {
        result += openInlineTag(f);
      }
    }

    result += char;
    prevFormats = currFormats.slice();
  }

  // Close remaining formats
  for (let j = prevFormats.length - 1; j >= 0; j--) {
    result += closeInlineTag(prevFormats[j]);
  }

  return result;
}

function openInlineTag(type: string) {
  if (type === 'bold') return '<strong>';
  if (type === 'italic') return '<em>';
  if (type === 'underline') return '<u>';
  return '';
}

function closeInlineTag(type: string) {
  if (type === 'bold') return '</strong>';
  if (type === 'italic') return '</em>';
  if (type === 'underline') return '</u>';
  return '';
}
